import Vue from "vue";
import createAuth0Client from "@auth0/auth0-spa-js";
import jwtDecode from 'jwt-decode';
import {getFromLocalStorage, saveToLocalStorage} from "../utils/browser-utils";

/** Define a default action to perform after authentication */
const DEFAULT_REDIRECT_CALLBACK = () => window.history.replaceState({}, document.title, window.location.pathname);

let instance;

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

/** Creates an instance of the Auth0 SDK. If one has already been created, it returns that instance */
const useAuth0 = ({
                    onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
                    redirectUri = window.location.origin,
                    store,
                    router,
                    ...options
                  }) => {
  if (instance) return instance;

  // The 'instance' is simply a Vue object
  instance = new Vue({
    data() {
      return {
        loading: true,
        isAuthenticated: false,
        user: {},
        auth0Client: null,
        error: null,
        permissions: []
      };
    },
    computed: {
      userData() {
        return this.user?.['https://musicapprentice.com.au/userdata'] ?? {};
      },
      appData() {
        return this.user?.['https://musicapprentice.com.au/appdata'] ?? {};
      },
      isTeacher() {
        return this.permissions.includes('manage:students');
      },
      isStudent() {
        return this.permissions.includes('manage:teachers');
      },
      isSubscribed() {
        return this.isStudent || this.isTeacher;
      },
      canManagerUsers() {
        return this.permissions.includes('manage:users');
      },
      canManageContent() {
        return this.permissions.includes('write:resource');
      },
      canManageSubscriptions() {
        return this.permissions.includes('manage:subscriptions');
      },
      canAccessStudio() {
        return this.permissions.includes('access:studio');
      },
      canAccessCms() {
        return this.permissions.includes('access:cms');
      }
    },
    methods: {
      async onLoggedIn() {
        // note: we're kind of in a different context here, this Auth0 instance is a different Vue instance to the main app...

        if(this.appName === 'studio') {
          await store.dispatch('app/initialise', {auth: this});

          if (store.state.app.onboardingTasks.length) {
            router.push({name: 'onboarding'}).catch(() => {
            });
          }
        }
      },
      async onUserChanged(newUser, oldUser) {
        // note: we're kind of in a different context here, this Auth0 instance is a different Vue instance to the main app...
        const isRefreshing = !!getFromLocalStorage('auth0_forcing_refresh', false);
        const hasNewUser = !!newUser?.sub;
        const hasOldUser = !!oldUser?.sub;
        const isLoggingIn = !hasOldUser && hasNewUser;
        const isLoggingOut = hasOldUser && !hasNewUser && !isRefreshing;
        const isDifferentUser = hasOldUser && hasNewUser && (newUser.sub !== oldUser.sub);

        this.isAuthenticated = hasNewUser;

        if(isLoggingIn) {
          await this.onLoggedIn();
        } else if(isDifferentUser) {
          await store.dispatch('app/initialise', { auth: this });
        } else if(isLoggingOut) {
          await this.onLoggedOut();
        }
      },
      async onLoggedOut() {
      },
      async doLogin(options = {}) {
        await this.loginWithRedirect(options);
      },
      async loginWithRedirect(options = {}) {
        try {
          await this.auth0Client.loginWithRedirect(options);
        } catch (e) {
          this.error = e;
          // eslint-disable-next-line
          console.error(e);
        }
      },
      async getDetailedTokenSilently() {
        const options = {
          detailedResponse: true,
          audience: window.AUTH0_AUDIENCE,
        };

        return this.getTokenSilently(options)
      },
      async getTokenSilently(options) {
        // note: this is sometimes called before the auth0client is initialised in the async created block
        while(!this.auth0Client) {
          await wait(250);
        }

        try {
          const token = await this.auth0Client.getTokenSilently(options);
          token.type = 'bearer';

          const decoded = jwtDecode(token.access_token);
          this.permissions = decoded.permissions;

          const oldUser = {...this.user};
          this.user = await this.auth0Client.getUser();
          await this.onUserChanged(this.user, oldUser);

          return token;
        } catch(err) {
          console.error(err);
          if (err.error === 'consent_required') {
            await this.auth0Client.loginWithRedirect({...options, prompt: 'consent', display: 'page'});
            return { message: "reattempting login" };
          }
        } finally {
          saveToLocalStorage('auth0_forcing_refresh', false);
        }
      },
      async forceTokenRefresh() {
        saveToLocalStorage('auth0_forcing_refresh', true);
        await this.auth0Client.cacheManager.clear(options.clientId);
        await this.getDetailedTokenSilently();
        saveToLocalStorage('auth0_forcing_refresh', false);
      },
      logout(o) {
        return this.auth0Client.logout(o);
      },
    },
    async created() {
      // initialise the auth0 client
      try {
        const auth0ClientOptions = { ...options, client_id: options.clientId, redirect_uri: redirectUri };
        this.auth0Client = await createAuth0Client(auth0ClientOptions);
      } catch(err) {
        console.log("could not create auth0 client")
        console.error(err);
      }

      // check if this is OAuth redirect
      try {
        if (window.location.search.includes("code=") && window.location.search.includes("state=")) {
          const {appState} = await this.auth0Client.handleRedirectCallback();
          this.error = null;
          onRedirectCallback(appState);
        }
      } catch (e) {
        this.error = e;
      } finally {
        // Initialize our internal authentication state
        const oldUser = {...this.user};
        this.user = await this.auth0Client.getUser();
        await this.onUserChanged(this.user, oldUser)

        if (this.user?.['https://musicapprentice.com.au/userdata']?.student?.firstName) {
          const student = this.user['https://musicapprentice.com.au/userdata'].student;
          this.user.name = `${student.firstName} ${student.lastName}`;
        } else if (this.user?.['https://musicapprentice.com.au/userdata']?.account?.name) {
          this.user.name = this.user['https://musicapprentice.com.au/userdata'].account.name;
        }

        this.loading = false;
      }
    }
  });

  return instance;
};

// Create a simple Vue plugin to expose the wrapper object throughout the application
export const Auth0Plugin = {
  install(Vue, options) {
    Vue.prototype.$auth = useAuth0(options);
  }
};